import { debounce } from 'lodash';
import styled from '@emotion/styled';
import useHover from 'react-use-hover';
import { Image } from 'react-bootstrap';
import PropTypes, { InferProps } from 'prop-types';
import React, { useEffect, useState } from 'react';

import useDebounce from '~/react-hooks/use-debounce';
import ProfilePopover from '~/App/Components/Popovers/Profile';

const HoverTrigger = styled.div`
  display: inline-block;
`;

const togglePopover = debounce(
  (hovered, onEnter, onExit) => (hovered ? onEnter() : onExit()),
  50
);

const profilePopoverTriggerPropTypes = {
  openPopover: PropTypes.func,
  closePopover: PropTypes.func,
  children: PropTypes.any,
  popoverIsHovered: PropTypes.bool,
  className: PropTypes.string as PropTypes.Validator<string | undefined>,
  style: PropTypes.any,
};

function ProfilePopoverTrigger({
  openPopover,
  closePopover,
  children,
  popoverIsHovered,
  className,
  style,
}: InferProps<typeof profilePopoverTriggerPropTypes>) {
  const [hovered, hoverProps] = useHover();
  const anyHovered = useDebounce(hovered || popoverIsHovered, 100);

  useEffect(
    () => togglePopover(anyHovered, openPopover, closePopover),
    [anyHovered]
  );

  return (
    <HoverTrigger {...hoverProps} className={className} style={style}>
      {children}
    </HoverTrigger>
  );
}

ProfilePopoverTrigger.propTypes = profilePopoverTriggerPropTypes;

const profileImageInnerPropTypes = {
  profile: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string.isRequired,
  }).isRequired,
  size: PropTypes.number.isRequired,
  className: PropTypes.string as PropTypes.Validator<string | undefined>,
  style: PropTypes.any,
};

function ProfileImageInner({
  profile,
  size,
  ...props
}: InferProps<typeof profileImageInnerPropTypes>) {
  return (
    <Image
      src={profile.image}
      circle
      alt={profile.name ? `Image of ${profile.name}` : 'Profile Image'}
      width={size}
      height={size}
      style={{ objectFit: 'cover' }}
      {...props}
    />
  );
}

ProfileImageInner.propTypes = profileImageInnerPropTypes;

const profileImagePropTypes = {
  profile: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string.isRequired,
  }).isRequired,
  size: PropTypes.number.isRequired,
  popover: PropTypes.bool,
  className: PropTypes.string as PropTypes.Validator<string | undefined>,
  style: PropTypes.any,
};

export default function ProfileImage({
  profile,
  size,
  popover = true,
  className,
  style,
}: InferProps<typeof profileImagePropTypes>) {
  const containerProps = {
    className,
    style,
  };

  const innerProps = {
    profile,
    size,
  };

  const inner = (
    <ProfileImageInner {...innerProps} {...(popover ? {} : containerProps)} />
  );
  const [popoverHovered, setPopoverHovered] = useState(false);

  if (!popover) {
    return inner;
  }

  return (
    <ProfilePopover profile={profile} onHover={setPopoverHovered}>
      {(props: any) => (
        <ProfilePopoverTrigger
          {...props}
          popoverIsHovered={popoverHovered}
          {...containerProps}
        >
          {inner}
        </ProfilePopoverTrigger>
      )}
    </ProfilePopover>
  );
}

ProfileImage.propTypes = profileImagePropTypes;
